import { FlexCol } from '@/components/common/Flex';
import { InfoButtonWithModal } from '@/components/common/InfoModal';
import { EyebrowHeader, Text } from '@/components/common/Typography';
import { toDollars } from '@/util/currency';
import { assert } from '@common/errors';
import { calculateForecastAmount } from '@common/forecasting';
import { getAnnualReturnUsingRiskScore } from '@common/goals';
import { useCurrentUser } from '@common/react/user';
import { TransactionSchedulePlan } from '@common/types';
import { dt } from '@common/util/dateTime';
import { isNumber } from '@common/util/types';
import { makeTickRange } from '@common/valueSeries';
import { useState } from 'react';
import { getSingleplayerOnboardingPlanChartEntities } from '../goalOnboardingChartHelper';
import GoalOnboardingPlanInputsAndChart from '../GoalOnboardingPlanInputsAndChart';
import { getAnnualReturnInfoForRiskScore } from '../helpers';
import { useOpenIraFlowContext } from './OpenIraFlow';
import OpenIraFlowStep from './OpenIraFlowStep';
import { useRetirementGoalContext } from './RetirementGoalDetailContainer';

const DEFAULT_INITIAL_DEPOSIT_PLAN: number = 0;
const DEFAULT_TRANSACTION_SCHEDULE_PLAN: TransactionSchedulePlan = {
    startDate: dt().toDateStr(),
    amount: 0,
    recurrence: { freq: 'MONTHLY', interval: 1 },
};

const OpenIraFlowPlanStep = () => {
    const { userInfo } = useCurrentUser();
    const { goal, currentRateInfo } = useRetirementGoalContext();
    const { setStep, info, updateInfo } = useOpenIraFlowContext();
    assert(info);
    const [initialDepositPlan, setInitialDepositPlan] = useState<number>(
        info.initialDepositPlan ?? DEFAULT_INITIAL_DEPOSIT_PLAN
    );
    const [transactionSchedulePlan, setTransactionSchedulePlan] =
        useState<TransactionSchedulePlan>(
            info.transactionSchedulePlan ?? DEFAULT_TRANSACTION_SCHEDULE_PLAN
        );
    const [isSubmitting, setIsSubmitting] = useState(false);

    if (!isNumber(info.riskScore)) {
        console.warn('Missing risk score; redirecting to PORTFOLIO');
        setStep('PORTFOLIO');
        return null;
    }

    if (!info.investmentType) {
        console.warn('Missing investment type; redirecting to PORTFOLIO');
        setStep('PORTFOLIO');
        return null;
    }

    const annualReturn = getAnnualReturnUsingRiskScore({
        investmentType: info.investmentType,
        riskScore: info.riskScore,
        currentRateInfo,
    });
    const { annualReturnLabel, annualReturnStr, annualReturnDisclosure } =
        getAnnualReturnInfoForRiskScore({
            investmentType: info.investmentType,
            riskScore: info.riskScore,
            currentRateInfo,
        });

    // Calculate potential value at age 65
    // FIXME: User isn't guaranteed to have a dob set here - this will result in an invalid date string
    const age65Date = dt(userInfo.dob).add(65, 'years').toDateStr();
    const potentialValueAtAge65 =
        // skip if less than 1 year until they turn 65
        userInfo.age >= 64
            ? undefined
            : calculateForecastAmount({
                  transactionSchedulePlans: [transactionSchedulePlan],
                  annualGrowthRate: annualReturn,
                  startAmount: initialDepositPlan ?? 0,
                  startDate: dt().toDateStr(),
                  targetDate: age65Date,
              });

    // Range end date is "10 years from now" or "age 70", whichever is later
    const in10YearsDate = dt().add(10, 'y').toDateStr();
    // FIXME: User isn't guaranteed to have a dob set here - this will result in an invalid date string
    const age70Date = dt(userInfo.dob).add(70, 'years').toDateStr();
    const rangeEndDate = age70Date > in10YearsDate ? age70Date : in10YearsDate;
    const range = makeTickRange(dt().toDateStr(), rangeEndDate, 'month');

    // N.B. We're using the "Singleplayer" helper here regardless of the
    // goal share status because we want a single-series chart.
    // TODO: Consider implementing a different helper function
    const chartEntities = getSingleplayerOnboardingPlanChartEntities({
        goal,
        targetDate: undefined, // TODO: Consider using expected retirement age
        annualReturn,
        initialDepositPlan,
        transactionSchedulePlan,
        range,
    });

    return (
        <OpenIraFlowStep
            headerTitle="Choose the timing and amount of your investment"
            onBack={() => {
                setIsSubmitting(true);
                updateInfo({
                    ...info,
                    initialDepositPlan,
                    transactionSchedulePlan,
                })
                    .then(() => setStep('PORTFOLIO'))
                    .finally(() => setIsSubmitting(false));
            }}
            onNext={() => {
                setIsSubmitting(true);
                updateInfo({
                    ...info,
                    initialDepositPlan,
                    transactionSchedulePlan,
                    lastCompletedRetirementStep: 'PLAN',
                })
                    .then(() => setStep('FUNDING'))
                    .finally(() => setIsSubmitting(false));
            }}
            isSubmitting={isSubmitting}
            footerMiddleContent={
                <>
                    <FlexCol>
                        <EyebrowHeader fontSize="sm">Risk score</EyebrowHeader>
                        <Text fontSize="lg">{`${info.riskScore} / 10`}</Text>
                    </FlexCol>
                    <FlexCol>
                        <EyebrowHeader fontSize="sm">
                            {annualReturnLabel}
                            <InfoButtonWithModal
                                buttonProps={{
                                    h: 4,
                                    w: 4,
                                    onClickEventParams: {
                                        location: 'Open IRA Flow Plan Step',
                                    },
                                }}
                                title={annualReturnLabel}
                            >
                                {annualReturnDisclosure}
                            </InfoButtonWithModal>
                        </EyebrowHeader>
                        <Text fontSize="lg">{annualReturnStr}</Text>
                    </FlexCol>
                    {isNumber(potentialValueAtAge65) && (
                        <FlexCol>
                            <EyebrowHeader fontSize="sm">
                                Potential value at 65 yrs old
                            </EyebrowHeader>
                            <Text fontSize="lg">
                                {toDollars(potentialValueAtAge65)}
                            </Text>
                        </FlexCol>
                    )}
                </>
            }
        >
            <GoalOnboardingPlanInputsAndChart
                goal={goal}
                initialDepositPlan={initialDepositPlan}
                onChangeInitialDepositPlan={setInitialDepositPlan}
                transactionSchedulePlan={transactionSchedulePlan}
                onChangeTransactionSchedulePlan={setTransactionSchedulePlan}
                partnerTransactionSchedulePlan={undefined}
                forecastCompletionDate={undefined}
                chartEntities={chartEntities}
                range={range}
            />
        </OpenIraFlowStep>
    );
};

export default OpenIraFlowPlanStep;
